import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import toast from 'react-hot-toast';
import Pagination from '../components/Pagination';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, id: null });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const recordsPerPage = 10;
  const [formData, setFormData] = useState({
    full_name: '', nick_name: '', email: '', user_name: '', password: '', user_group_id: '', audit_record: false, delete_status: true
  });

  const userRoles = [
    { id: 1, name: 'Admin' },
    { id: 2, name: 'Staff' },
    { id: 3, name: 'Manager' },
    { id: 4, name: 'Super Admin' }
  ];

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/users`, { withCredentials: true });
      setUsers(res.data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to fetch users');
    }
  };


  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
  };

  const handleEdit = (user) => {
    setFormData({
      full_name: user.full_name || '',
      nick_name: user.nick_name || '',
      email: user.email || '',
      user_name: user.user_name || '',
      password: user.password || '',
      user_group_id: user.user_group_id || '',
      audit_record: user.audit_record === 1,
      delete_status: user.delete_status === 0
    });
    setEditingId(user.user_id);
    setShowForm(true);
  };

  const triggerDelete = (id) => {
    setDeleteModal({ isOpen: true, id });
  };

  const confirmDelete = async () => {
    const id = deleteModal.id;
    setDeleteModal({ isOpen: false, id: null });
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/users/${id}`, { withCredentials: true });
      toast.success("User deleted successfully!");
      fetchUsers();
    } catch (err) {
      toast.error("Error deleting user.");
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        delete_status: formData.delete_status ? 0 : 1 // 0 means active/enabled
      };
      if (editingId) {
        await axios.put(`${process.env.REACT_APP_API_URL}/api/users/${editingId}`, payload, { withCredentials: true });
        toast.success("User updated successfully!");
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/users`, payload, { withCredentials: true });
        toast.success("User created successfully!");
      }
      setShowForm(false);
      setEditingId(null);
      setFormData({ full_name: '', nick_name: '', email: '', user_name: '', password: '', user_group_id: '', audit_record: false, delete_status: true });
      fetchUsers();
    } catch (err) {
      toast.error("Error saving user.");
    }
  };

  const getRoleName = (id) => {
    const role = userRoles.find(r => r.id === parseInt(id));
    return role ? role.name : '-';
  };

  const filteredUsers = users.filter(user =>
    (user.user_name && user.user_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (user.full_name && user.full_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const totalPages = Math.ceil(filteredUsers.length / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredUsers.slice(indexOfFirstRecord, indexOfLastRecord);

  return (
    <Layout title="User Management" subtitle="Manage application users and login access">
      <div className="bg-slate-900/20 border border-slate-900 rounded-3xl p-6">

        {showForm ? (
          <div className="bg-slate-900/50 p-6 rounded-2xl border border-slate-800 animate-in fade-in zoom-in duration-200">
            <h3 className="font-bold text-lg text-emerald-500 mb-6 border-b border-slate-800 pb-3">{editingId ? 'Edit User' : 'Add New User'}</h3>
            <form onSubmit={handleSave} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <input required type="text" name="full_name" placeholder="Full Name" value={formData.full_name} onChange={handleChange} className="bg-slate-950 border border-slate-800 p-3 rounded-xl text-white text-sm w-full outline-none focus:border-emerald-500 transition-colors" />
                <input type="text" name="nick_name" placeholder="Nick Name" value={formData.nick_name} onChange={handleChange} className="bg-slate-950 border border-slate-800 p-3 rounded-xl text-white text-sm w-full outline-none focus:border-emerald-500 transition-colors" />
                <input required type="email" name="email" placeholder="Email" value={formData.email} onChange={handleChange} className="bg-slate-950 border border-slate-800 p-3 rounded-xl text-white text-sm w-full outline-none focus:border-emerald-500 transition-colors" />
                <input required type="text" name="user_name" placeholder="User Name" value={formData.user_name} onChange={handleChange} className="bg-slate-950 border border-slate-800 p-3 rounded-xl text-white text-sm w-full outline-none focus:border-emerald-500 transition-colors" />

                <input required type="password" name="password" placeholder="Password" value={formData.password} onChange={handleChange} className="bg-slate-950 border border-slate-800 p-3 rounded-xl text-white text-sm w-full outline-none focus:border-emerald-500 transition-colors" />

                <select required name="user_group_id" value={formData.user_group_id} onChange={handleChange} className="bg-slate-950 border border-slate-800 p-3 rounded-xl text-white text-sm w-full outline-none focus:border-emerald-500 transition-colors appearance-none">
                  <option value="" disabled hidden>User Role</option>
                  {userRoles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>

                <div className="flex items-center gap-2 px-3 bg-slate-950 border border-slate-800 rounded-xl text-white text-sm w-full h-full">
                  <input type="checkbox" name="audit_record" id="audit_record" checked={formData.audit_record} onChange={handleChange} className="w-4 h-4 accent-emerald-500 cursor-pointer" />
                  <label htmlFor="audit_record" className="text-sm text-gray-400 cursor-pointer select-none">Audit Record</label>
                </div>

                <select name="delete_status" value={formData.delete_status} onChange={(e) => setFormData({ ...formData, delete_status: e.target.value === 'true' })} className="bg-slate-950 border border-slate-800 p-3 rounded-xl text-white text-sm w-full outline-none focus:border-emerald-500 transition-colors appearance-none">
                  <option value="true">Enable</option>
                  <option value="false">Disable</option>
                </select>
              </div>

              <div className="flex gap-4 pt-6 border-t border-slate-800">
                <button type="button" onClick={() => { setShowForm(false); setEditingId(null); }} className="w-1/3 bg-slate-800 text-white p-3 rounded-xl font-bold hover:bg-slate-700 transition">Cancel</button>
                <button type="submit" className="w-2/3 bg-emerald-500 text-black font-bold p-3 rounded-xl hover:bg-emerald-400 transition">Save User</button>
              </div>
            </form>
          </div>
        ) : (
          <>
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-bold text-white">System Users</h3>
              <div className="flex gap-3">
                <input
                  type="text"
                  placeholder="Search users..."
                  className="bg-slate-950 border border-slate-800 p-2 rounded-xl text-white text-sm outline-none focus:border-emerald-500"
                  value={searchTerm}
                  onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                />
                <button onClick={() => {
                  setFormData({ full_name: '', nick_name: '', email: '', user_name: '', password: '', user_group_id: '', audit_record: false, delete_status: true });
                  setShowForm(true);
                }} className="bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded-xl text-sm font-bold transition">
                  + Add User
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-sm text-gray-300">
                <thead>
                  <tr className="border-b border-slate-800 text-gray-500 font-mono">
                    <th className="pb-3 w-16">S.No</th>
                    <th className="pb-3">User Name</th>
                    <th className="pb-3">Full Name</th>
                    <th className="pb-3">Email</th>
                    <th className="pb-3">Role</th>
                    <th className="pb-3">Status</th>
                    <th className="pb-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/50">
                  {currentRecords.map((user, index) => (
                    <tr key={user.user_id} className="hover:bg-slate-800/20">
                      <td className="py-4 text-gray-500 font-mono text-xs">{indexOfFirstRecord + index + 1}</td>
                      <td className="py-4 font-semibold text-white">{user.user_name}</td>
                      <td className="py-4">{user.full_name || '-'}</td>
                      <td className="py-4">{user.email || '-'}</td>
                      <td className="py-4">{getRoleName(user.user_group_id)}</td>
                      <td className="py-4">
                        <span className={`px-2 py-1 rounded-md text-xs ${user.delete_status === 0 ? 'bg-emerald-500/10 text-emerald-400' : 'bg-rose-500/10 text-rose-400'}`}>
                          {user.delete_status === 0 ? 'Active' : 'Disabled'}
                        </span>
                      </td>
                      <td className="py-4">
                        <div className="flex justify-end gap-3">
                          <button onClick={() => handleEdit(user)} className="text-blue-400 hover:text-blue-300 transition" title="Edit">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path></svg>
                          </button>
                          <button onClick={() => triggerDelete(user.user_id)} className="text-rose-400 hover:text-rose-300 transition" title="Delete">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {currentRecords.length === 0 && (
                    <tr><td colSpan="7" className="text-center py-6 text-gray-500">No users found.</td></tr>
                  )}
                </tbody>
              </table>
              <Pagination currentPage={currentPage} totalPages={totalPages} setCurrentPage={setCurrentPage} totalItems={filteredUsers.length} />
            </div>
          </>
        )}
      </div>

      {deleteModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-rose-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Confirm Deletion</h3>
              <p className="text-gray-400 text-sm mb-6">Are you sure you want to delete this user? This action cannot be undone.</p>
              <div className="flex justify-center gap-3">
                <button onClick={() => setDeleteModal({ isOpen: false, id: null })} className="px-5 py-2 text-gray-300 hover:text-white transition-colors text-sm font-semibold">Cancel</button>
                <button onClick={confirmDelete} className="px-5 py-2 bg-rose-600 hover:bg-rose-500 text-white rounded-xl font-bold text-sm transition-colors shadow-lg shadow-rose-600/20">Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default UserManagement;
